// src/scripts/useStorageFiles.js
import { ref as vueRef, computed } from "vue";
import { storage } from "@/firebase";
import { ref as storageRef, listAll, getDownloadURL, getMetadata } from "firebase/storage";

export function useStorageFiles() {
  const currentPath = vueRef("");     // e.g. "doc-library/security"
  const folders = vueRef([]);         // [{ name, fullPath }]
  const files = vueRef([]);           // [{ name, fullPath, url, contentType, size, updated }]
  const loading = vueRef(false);
  const error = vueRef(null);

  const normalize = (p) => {
    if (!p) return "";
    return String(p).trim().replace(/^\/+/, "").replace(/\/+$/, "");
  };

  const listFolder = async (path = "") => {
    loading.value = true;
    error.value = null;
    folders.value = [];
    files.value = [];

    try {
      if (!storage) {
        throw new Error(
          "Firebase Storage is undefined. Ensure `export const storage = getStorage(app)` exists in src/firebase.js"
        );
      }

      const cleanPath = normalize(path);
      currentPath.value = cleanPath;

      const folderRef = cleanPath ? storageRef(storage, cleanPath) : storageRef(storage);
      const res = await listAll(folderRef);

      // folders
      folders.value = res.prefixes
        .map((p) => ({ name: p.name, fullPath: p.fullPath }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

      // files
      const fileEntries = await Promise.all(
        res.items.map(async (itemRef) => {
          const [url, meta] = await Promise.all([
            getDownloadURL(itemRef),
            getMetadata(itemRef),
          ]);

          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            url,
            contentType: meta.contentType ?? "",
            size: meta.size ?? 0,
            updated: meta.updated ?? "",
          };
        })
      );

      fileEntries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      files.value = fileEntries;
    } catch (e) {
      error.value = e;
    } finally {
      loading.value = false;
    }
  };

  const breadcrumbs = computed(() => {
    const p = normalize(currentPath.value);
    if (!p) return [];

    const parts = p.split("/").filter(Boolean);
    const crumbs = [];
    for (let i = 0; i < parts.length; i++) {
      crumbs.push({
        label: parts[i],
        path: parts.slice(0, i + 1).join("/"),
      });
    }
    return crumbs;
  });

  const filtered = (queryRef) =>
    computed(() => {
      const q = (queryRef?.value ?? "").trim().toLowerCase();
      const f = folders.value;
      const fi = files.value;

      if (!q) return { folders: f, files: fi };

      return {
        folders: f.filter((x) => x.name.toLowerCase().includes(q) || x.fullPath.toLowerCase().includes(q)),
        files: fi.filter((x) => {
          const name = (x.name ?? "").toLowerCase();
          const path = (x.fullPath ?? "").toLowerCase();
          const type = (x.contentType ?? "").toLowerCase();
          return name.includes(q) || path.includes(q) || type.includes(q);
        }),
      };
    });

  const goUp = async () => {
    const p = normalize(currentPath.value);
    if (!p) return listFolder("");
    const parts = p.split("/").filter(Boolean);
    parts.pop();
    return listFolder(parts.join("/"));
  };

  return {
    currentPath,
    folders,
    files,
    loading,
    error,
    listFolder,
    breadcrumbs,
    filtered,
    goUp,
  };
}
